import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Public page hit by clicking the link in the verification email. Reads
 * ?token=... and POSTs it to the backend. Backend marks the user verified
 * AND returns a fresh session JWT — this is the hard-gate "first login"
 * moment. We persist the token + user via finalizeSession, then auto-route
 * to /dashboard. Three visible states: verifying, success (brief), error.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const { finalizeSession } = useAuth();
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setMessage('Missing verification token in the URL.'); return; }
    authAPI.verifyEmail(token)
      .then(({ data }) => {
        setState('success');
        // Backend returns the full AuthResponse (token + user fields). Persist
        // it so the user is logged in, then send them to the dashboard.
        if (data?.token) {
          finalizeSession(data);
          setTimeout(() => navigate('/dashboard', { replace: true }), 800);
        }
      })
      .catch(e => {
        setState('error');
        setMessage(e.response?.data?.error || 'Could not verify this link. It may be expired or already used.');
      });
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <Link to="/" style={brand}>
          <span style={brandMark}>🎬</span>
          <span style={brandName}>CreatorKhata</span>
        </Link>

        {state === 'verifying' && (
          <>
            <div style={iconWrap}>⏳</div>
            <h1 style={title}>Verifying your email…</h1>
            <p style={sub}>Hang on a second.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div style={iconWrap}>✅</div>
            <h1 style={title}>Email verified</h1>
            <p style={sub}>You're all set. Sending you to your dashboard…</p>
            <Link to="/dashboard" style={ctaPrimary}>Go to dashboard →</Link>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={iconWrap}>⚠️</div>
            <h1 style={title}>Verification failed</h1>
            <p style={sub}>{message}</p>
            <Link to="/" style={ctaSecondary}>Back to home</Link>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', background: '#fafafa',
  fontFamily: "'Inter', sans-serif",
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1.5rem',
};
const cardStyle = {
  background: 'white', border: '1px solid #e4e4e7',
  borderRadius: 20, padding: '2.5rem 2rem',
  maxWidth: 440, width: '100%', textAlign: 'center',
  boxShadow: '0 10px 30px rgba(10,10,12,0.06)',
};
const brand = {
  display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
  textDecoration: 'none', color: '#0a0a0c',
  marginBottom: '1.5rem',
};
const brandMark = {
  width: 32, height: 32, borderRadius: 9,
  background: 'linear-gradient(135deg,#6366f1,#ec4899)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '0.85rem',
};
const brandName = {
  fontFamily: "'Syne', sans-serif",
  fontWeight: 800, fontSize: '1.05rem',
  letterSpacing: '-0.03em',
};
const iconWrap = { fontSize: '2.5rem', marginBottom: '0.5rem' };
const title = {
  fontFamily: "'Syne', sans-serif",
  fontWeight: 800, fontSize: '1.5rem',
  letterSpacing: '-0.02em', color: '#0a0a0c',
  margin: '0.5rem 0 0.5rem',
};
const sub = { color: '#52525b', fontSize: '0.95rem', lineHeight: 1.55, margin: '0 0 1.5rem' };
const ctaPrimary = {
  display: 'inline-block', textDecoration: 'none',
  background: '#0a0a0c', color: 'white',
  fontWeight: 700, padding: '0.85rem 1.5rem',
  borderRadius: 12, fontSize: '0.95rem',
};
const ctaSecondary = {
  display: 'inline-block', textDecoration: 'none',
  background: 'white', color: '#0a0a0c',
  border: '1.5px solid #e4e4e7',
  fontWeight: 600, padding: '0.85rem 1.5rem',
  borderRadius: 12, fontSize: '0.95rem',
};
